import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FileText,
  Info,
  Search,
  BarChart2,
  Bot,
  Lock,
  FolderOpen,
  Globe,
  RefreshCw,
  Plus,
  Minus,
  ArrowUp,
  Mail,
} from "lucide-react";
import "./Privacy.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TEAL = "#00D1C1";

const SectionHeading = ({ icon: Icon, iconSrc, title, children }) => (
  <div className="flex flex-col gap-5">
    <div className="privacy-section-icon">
      {iconSrc ? (
        <img src={iconSrc} alt={title} className="w-full h-full object-contain" width={48} height={48} />
      ) : (
        <Icon className="w-6 h-6" style={{ color: TEAL }} />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-4">
        <div className="privacy-section-bar" />
        <h2 className="text-[32px] font-bold leading-[54px] text-black">{title}</h2>
      </div>
      {children}
    </div>
  </div>
);

const BulletItem = ({ title, desc }) => (
  <li className="flex items-start gap-3 pl-0 list-none">
    <div className="privacy-bullet flex-shrink-0 mt-2" />
    <span className="text-black text-[17px] leading-[29px]">
      <strong className="font-semibold">{title}:</strong> {desc}
    </span>
  </li>
);

const BulletText = ({ text }) => (
  <li className="flex items-start gap-3 pl-0 list-none">
    <div className="privacy-bullet flex-shrink-0 mt-2" />
    <span className="text-black text-[17px] leading-[29px]">{text}</span>
  </li>
);

const Privacy = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [faqOpen, setFaqOpen] = useState({ 0: true, 1: false });

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  const faqs = [
    {
      q: "What data does TeamGrid collect?",
      a: "TeamGrid collects only work-related activity signals such as app usage duration, focus time, and project engagement. It does not collect content, screenshots, webcam data, or personal files.",
    },
    {
      q: "How is my data protected?",
      a: "We use encryption at rest (AES-256) and in transit (TLS 1.3), role-based access controls, and enterprise-grade infrastructure. We never sell or rent your data.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | TeamGrid</title>
        <meta name="description" content="TeamGrid privacy policy. How we collect, use, and protect your data. Privacy-first workforce intelligence." />
        <link rel="canonical" href="https://teamgrid.ai/privacy-policy" />
      </Helmet>
      <div className="privacy-container">
      <PublicHeader />
      <div className="pt-16 sm:pt-18 md:pt-20 bg-white">
        {/* Main content - aligned with header (logo left, CTA right) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="privacy-main max-w-[1065px] mx-auto py-[80px] flex flex-col gap-[47px]">
          {/* Hero - spec: badge 8px 16px, radius 20px; H1 48px; subtitle 24px; quote bar */}
          <div className="privacy-hero flex flex-col items-center text-center gap-5">
            <div
              className="privacy-hero-badge inline-flex items-center gap-2 border"
              style={{
                padding: "8px 16px",
                borderRadius: 20,
                background: "rgba(0, 209, 193, 0.06)",
                borderColor: "rgba(0, 209, 193, 0.15)",
              }}
            >
              <FileText className="w-4 h-4 flex-shrink-0" style={{ color: TEAL }} />
              <span className="font-semibold text-sm leading-6" style={{ color: TEAL }}>
                Last updated: May 2026
              </span>
            </div>
            <h1
              className="font-extrabold text-black text-center"
              style={{ fontSize: 48, lineHeight: "82px", letterSpacing: "-0.96px" }}
            >
              Privacy Policy
            </h1>
            <p className="text-center max-w-[544px] whitespace-nowrap" style={{ fontSize: 24, lineHeight: "43px", color: "rgba(0,0,0,0.6)" }}>
              Your data. Your control. Complete transparency.
            </p>
            <div
              className="privacy-hero-quote inline-flex items-center justify-center gap-3 rounded-xl border-2 px-6 py-3 w-full max-w-[714px]"
              style={{
                background: "rgba(0, 209, 193, 0.05)",
                borderColor: "rgba(0, 209, 193, 0.3)",
                borderRadius: 12,
              }}
            >
              <img src="/new_pp_1.webp" alt="Trust and privacy" className="w-6 h-6 flex-shrink-0" width={24} height={24} />
              <p className="font-semibold text-black text-center" style={{ fontSize: 18, lineHeight: "31px" }}>
                Built for trust, not control. Signal over surveillance. Patterns, not people.
              </p>
            </div>
          </div>

          {/* Introduction */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={Info} title="Introduction">
              <p className="text-black text-[17px] leading-[31px] mb-4">
                At TeamGrid, your privacy is fundamental to our product design. We believe in transparency, trust, and responsible use of AI-powered insights.
              </p>
              <p className="text-black text-[17px] leading-[31px] mb-6">
                TeamGrid is an AI-powered workforce analytics platform designed to help organizations understand team productivity, identify work patterns, and surface opportunities for improvement and all without surveillance.
              </p>
              <div
                className="rounded-lg p-6"
                style={{ background: "rgba(0, 209, 193, 0.05)" }}
              >
                <p className="font-semibold mb-3 text-[18px] leading-[32px]" style={{ color: TEAL }}>
                  No spying. No screenshots. No typing logged.
                </p>
                <p className="text-black text-[17px] leading-[31px]">
                  TeamGrid focuses on patterns, insights and not surveillance. We do not take screenshots, log what you type, access personal messages, or use webcams and microphones.
                </p>
              </div>
            </SectionHeading>
          </section>

          {/* 1. Information We Collect */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={Search} iconSrc="/new_pp_2.webp" title="1. Information We Collect">
              <p className="text-black text-[17px] leading-[31px] mb-4">
                TeamGrid collects only the information required to generate productivity insights. This includes:
              </p>
              <ul className="flex flex-col gap-3 mb-6">
                <BulletItem title="Application usage metadata" desc="Which applications you use, how time is distributed across different tools" />
                <BulletItem title="Task activity duration" desc="Work session durations and time distribution" />
                <BulletItem title="Project and role-based work patterns" desc="Your job title, department, and project assignments" />
                <BulletItem title="AI-generated summaries and insights" desc="Patterns our AI identifies and suggested improvements" />
                <BulletItem title="Google account profile information" desc="Name and email address when you authenticate via Google Sign-In" />
                <BulletItem title="Gmail data (metadata-only access)" desc="When you connect Email Insights, we access only Gmail metadata — sender, recipient, timestamps, labels, and thread/message identifiers — strictly to power volume analytics, response-time, top contacts, and communication-loop tracking. While the gmail.metadata scope technically provides access to subject lines, TeamGrid explicitly does not fetch, process, or store them. We never send, modify, or delete email." />
              </ul>
              <div
                className="privacy-dont-collect rounded-xl border p-7 flex flex-col gap-5"
                style={{ background: "rgba(255,255,255,0.02)", borderColor: "#FFFFFF" }}
              >
                <h3 className="font-semibold text-[20px] leading-[34px]" style={{ color: TEAL }}>
                  ✗ What We DON'T Collect
                </h3>
                <ul className="flex flex-col gap-3">
                  <BulletItem title="No screenshots" desc="We never capture what's on your screen" />
                  <BulletItem title="No typing logged" desc="We don't log what you type" />
                  <BulletItem title="No chats, documents, or personal files" desc="We don't access chat platforms, documents, or files on your device. Gmail content (subject lines and message bodies) is never read — Email Insights uses metadata-only access" />
                  <BulletItem title="No webcam or microphone" desc="We never access your camera or audio" />
                  <BulletItem title="No passwords" desc="We never collect or store your passwords" />
                </ul>
              </div>
            </SectionHeading>
          </section>

          {/* 2. How Data Is Used */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={BarChart2} iconSrc="/new_pp_3.webp" title="2. How Data Is Used">
              <p className="text-black text-[17px] leading-[31px] mb-4">
                Collected data is used solely for:
              </p>
              <ul className="flex flex-col gap-3 mb-6">
                <BulletItem title="Generating daily and monthly AI summaries" desc="Automated reports highlighting productivity trends" />
                <BulletItem title="Identifying strengths, inefficiencies, and improvement areas" desc="Pattern recognition to surface opportunities" />
                <BulletItem title="Recommending AI automations and workflow optimizations" desc="Actionable suggestions based on actual work patterns" />
                <BulletItem title="Providing organizational performance insights" desc="Helping teams understand where time goes and how to improve" />
              </ul>
              <div className="rounded-lg p-6" style={{ background: "rgba(0, 209, 193, 0.05)" }}>
                <p className="text-black text-[17px] leading-[31px]">
                  Every piece of data we collect serves one purpose: helping your team work better. We analyze work patterns to identify trends and opportunities and not to judge individuals.
                </p>
              </div>
            </SectionHeading>
          </section>

          {/* 3. Google API Services & Email Insights */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={Mail} title="3. Google API Services & Email Insights">
              <p className="text-black text-[17px] leading-[31px] mb-4">
                TeamGrid's Email Insights feature uses Google OAuth to access Gmail data in a strictly <strong>metadata-only</strong> manner. We request the <code>auth/gmail.metadata</code> scope — one of Google's most restricted Gmail scopes, which does <em>not</em> grant access to message bodies or attachments. While the <code>gmail.metadata</code> scope technically provides access to subject lines, TeamGrid explicitly does not fetch, process, or store subject lines in any form. By connecting your Google account, the following applies:
              </p>

              <h3 className="font-semibold text-[20px] leading-[34px] mb-3" style={{ color: TEAL }}>
                3.1 What We Access
              </h3>
              <p className="text-black text-[17px] leading-[31px] mb-3">
                With your explicit consent, we access only the following Gmail metadata fields — and nothing else — through the Gmail API:
              </p>
              <ul className="flex flex-col gap-3 mb-6">
                <BulletItem title="Sender and recipient addresses" desc="The From / To headers — used to build top-contact lists and response-time analysis" />
                <BulletItem title="Timestamps" desc="When each message was sent or received — used for volume trends, heatmaps, and day-of-week patterns" />
                <BulletItem title="Message headers (metadata only)" desc="Routing and delivery headers such as From, To, Date, Message-ID, and In-Reply-To. We never access the Subject header or any body content" />
                <BulletItem title="Gmail labels" desc="Whether a message is in INBOX, SENT, etc. — used to classify direction and skip spam/trash/drafts" />
                <BulletItem title="Thread and message identifiers" desc="Opaque IDs (Message-ID, In-Reply-To, threadId) used to reconstruct communication loops and reply chains" />
              </ul>
              <div className="rounded-lg p-6 mb-6" style={{ background: "rgba(0, 209, 193, 0.05)" }}>
                <p className="font-semibold text-[18px] leading-[32px] mb-2" style={{ color: TEAL }}>
                  We never read Subject lines or message bodies.
                </p>
                <p className="text-black text-[17px] leading-[31px]">
                  While the <code>gmail.metadata</code> scope technically provides access to subject lines, TeamGrid explicitly does not fetch, process, or store subject lines in any form.
                </p>
              </div>

              <h3 className="font-semibold text-[20px] leading-[34px] mb-3" style={{ color: TEAL }}>
                3.2 What We Will Never Do
              </h3>
              <p className="text-black text-[17px] leading-[31px] mb-3">We do <strong>NOT</strong>:</p>
              <ul className="flex flex-col gap-3 mb-6">
                <BulletText text="Fetch, process, or store subject lines in any form — even though the gmail.metadata scope would technically allow it, we deliberately never request them" />
                <BulletText text="Read message bodies, attachments, or any email content — our scope does not grant access to it" />
                <BulletText text="Modify, send, delete, or otherwise alter any of your emails — our scope does not permit any write operations" />
                <BulletText text="Use your Gmail metadata, in any form, to train AI or machine learning models — neither ours nor any third party's" />
                <BulletText text="Use Gmail metadata for advertising, profiling, resale, or any purpose unrelated to the Email Insights features described above" />
                <BulletText text="Sell, rent, or share Google user data with third parties, except secure cloud infrastructure providers strictly necessary to operate the Service" />
              </ul>

              <h3 className="font-semibold text-[20px] leading-[34px] mb-3" style={{ color: TEAL }}>
                3.3 Limited Use Compliance
              </h3>
              <p className="text-black text-[17px] leading-[31px] mb-6">
                Our use and transfer of information received from Google APIs adheres to the{" "}
                <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: TEAL }}>
                  Google API Services User Data Policy
                </a>
                , including the Limited Use requirements.
              </p>

              <h3 className="font-semibold text-[20px] leading-[34px] mb-3" style={{ color: TEAL }}>
                3.4 Data Storage, Processing & Hosting
              </h3>
              <ul className="flex flex-col gap-3 mb-6">
                <BulletText text="Gmail metadata is processed in real time through secure, encrypted API calls and is NOT permanently stored on our servers" />
                <BulletText text="No subject lines, message bodies, or attachments are ever fetched, processed, transmitted, logged, or stored by TeamGrid" />
                <BulletText text="Any temporarily cached metadata is encrypted at rest using AES-256, and refreshed or discarded on a short rolling window" />
                <BulletText text="Google OAuth tokens (access and refresh tokens) are stored encrypted using AES-256-GCM" />
                <BulletText text="All processing of Gmail metadata occurs on infrastructure hosted in the United States and the European Union" />
                <BulletText text="Data is encrypted in transit using TLS 1.3" />
              </ul>

              <h3 className="font-semibold text-[20px] leading-[34px] mb-3" style={{ color: TEAL }}>
                3.5 User Control & Revocation
              </h3>
              <ul className="flex flex-col gap-3 mb-6">
                <BulletText text="You can disconnect your Google account at any time from within the Email Insights settings in TeamGrid" />
                <BulletText text="Upon disconnection, we immediately revoke the OAuth tokens and delete all stored tokens and cached Gmail metadata" />
                <BulletText text="You can also revoke access directly from your Google Account Permissions page at myaccount.google.com/permissions" />
              </ul>

              <div className="rounded-lg p-6" style={{ background: "rgba(0, 209, 193, 0.05)" }}>
                <p className="text-black text-[17px] leading-[31px]">
                  For any questions or data deletion requests related to Email Insights, contact us at{" "}
                  <a href="mailto:hello@teamgrid.ai" className="underline" style={{ color: TEAL }}>hello@teamgrid.ai</a>.
                </p>
              </div>
            </SectionHeading>
          </section>

          {/* 4. AI & Automation Transparency */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={Bot} iconSrc="/new_pp_4.webp" title="4. AI & Automation Transparency">
              <p className="text-black text-[17px] leading-[31px] mb-6">
                TeamGrid uses AI to analyze behavioral patterns and workloads. AI outputs are advisory in nature and intended to support better decisions and not replace human judgment.
              </p>
              <div
                className="privacy-ai-box rounded-xl border p-6 flex flex-col gap-4"
                style={{ background: "rgba(255,255,255,0.02)", borderColor: "#FFFFFF" }}
              >
                <h3 className="font-semibold text-[20px] leading-[34px]" style={{ color: TEAL }}>
                  How Our AI Works
                </h3>
                <p className="font-semibold text-black text-[17px] leading-[31px]">Patterns, not content:</p>
                <p className="text-black text-[17px] leading-[31px] -mt-2">We analyze usage patterns, not the content of your work.</p>
                <p className="font-semibold text-black text-[17px] leading-[31px]">Insights, not surveillance:</p>
                <p className="text-black text-[17px] leading-[31px] -mt-2">Our AI generates human-readable insights to support decisions, not raw activity logs.</p>
                <p className="font-semibold text-black text-[17px] leading-[31px]">Role-aware:</p>
                <p className="text-black text-[17px] leading-[31px] -mt-2">Recommendations are tailored to your role and context.</p>
              </div>
            </SectionHeading>
          </section>

          {/* 5. Data Security */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={Lock} iconSrc="/lock_privacy_policy.webp" title="5. Data Security">
              <p className="text-black text-[17px] leading-[31px] mb-4">
                We implement industry-standard security practices including encryption, restricted access controls, and secure infrastructure to protect your data.
              </p>
              <ul className="flex flex-col gap-3">
                <BulletItem title="Secure Infrastructure" desc="Hosted on enterprise-grade, secure cloud servers with a 99.9% uptime SLA" />
                <BulletItem title="Encrypted Storage" desc="All data encrypted at rest (AES-256) and in transit (TLS 1.3)" />
                <BulletItem title="Access Controls" desc="Role-based access controls; only essential TeamGrid employees can access infrastructure" />
                <BulletItem title="Best Practices" desc="Regular security audits, penetration testing, and continuous monitoring" />
              </ul>
              <div className="rounded-lg p-6 mt-6" style={{ background: "rgba(0, 209, 193, 0.05)" }}>
                <p className="font-semibold text-[18px] leading-[32px] mb-2" style={{ color: TEAL }}>
                  We do not sell user data to third parties.
                </p>
                <p className="text-black text-[17px] leading-[31px]">
                  TeamGrid never sells, rents, or trades your data — including any data received from Google APIs. We share data only with secure cloud infrastructure providers strictly necessary to operate the Service, under contractual confidentiality and data-protection obligations.
                </p>
              </div>
            </SectionHeading>
          </section>

          {/* 6. Data Ownership */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={FolderOpen} iconSrc="/new_pp_point5.webp" title="6. Data Ownership">
              <div className="rounded-lg p-6 mb-4" style={{ background: "rgba(0, 209, 193, 0.05)" }}>
                <p className="font-semibold text-[18px] leading-[32px] mb-3" style={{ color: TEAL }}>
                  Your organization owns its data. Period.
                </p>
                <p className="text-black text-[17px] leading-[31px]">
                  TeamGrid is a data processor, not a data owner. We process your data solely to provide the service you've contracted us for. We don't sell it, rent it, or use it for our own purposes.
                </p>
              </div>
              <p className="text-black text-[17px] leading-[31px] mb-3">What this means for you:</p>
              <ul className="flex flex-col gap-3">
                <BulletText text="You can export your data anytime in standard formats" />
                <BulletText text="You can delete your data by contacting us and we'll remove it within 30 days" />
                <BulletText text="You control who sees what through role-based permissions" />
                <BulletText text="If you leave TeamGrid, we retain data for 90 days, then permanently delete it" />
              </ul>
            </SectionHeading>
          </section>

          {/* 7. Remote & Hybrid Teams */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={Globe} iconSrc="/new_icon_6.webp" title="7. Remote & Hybrid Teams">
              <p className="text-black text-[17px] leading-[31px]">
                TeamGrid is designed for modern teams working remotely, hybrid, or across multiple locations to maintain clarity without intrusion.
              </p>
              <p className="text-black text-[17px] leading-[31px]">
                Our platform provides the insights distributed teams need to stay aligned, without the micromanagement that erodes trust.
              </p>
            </SectionHeading>
          </section>

          {/* 8. Updates to This Policy */}
          <section className="flex flex-col gap-5">
            <SectionHeading icon={RefreshCw} title="8. Updates to This Policy">
              <p className="text-black text-[17px] leading-[31px] mb-4">
                We may update this Privacy Policy as the platform evolves. Any changes will be reflected on this page with an updated "Last modified" date.
              </p>
              <ul className="flex flex-col gap-3">
                <BulletText text="For minor changes (typos, clarifications), we'll just post the update" />
                <BulletText text="For significant changes (new data collection, policy shifts), we'll email you 30 days in advance" />
                <BulletText text="You'll be able to see previous versions of this policy on request" />
              </ul>
            </SectionHeading>
          </section>
        </div>

        {/* FAQ Section - spec: padding 80px, gap 64px, Got Questions badge radius 100px */}
        <section className="privacy-faq-section py-20 px-6 flex flex-col items-center" style={{ background: "rgba(0, 0, 0, 0.05)" }}>
          <div className="max-w-[1200px] w-full flex flex-col items-center gap-16">
            <div className="flex flex-col items-center gap-5 text-center">
              <div
                className="privacy-faq-badge inline-flex items-center justify-center border px-5 py-2"
                style={{
                  borderRadius: 100,
                  background: "rgba(0, 209, 193, 0.06)",
                  borderColor: "rgba(0, 209, 193, 0.15)",
                }}
              >
                <span
                  className="font-semibold uppercase tracking-wide"
                  style={{ fontSize: 12, lineHeight: "19px", letterSpacing: "0.72px", color: TEAL }}
                >
                  Got Questions?
                </span>
              </div>
              <h2 className="font-extrabold text-black" style={{ fontSize: 48, lineHeight: "82px", letterSpacing: "-0.96px" }}>
                Frequently Asked Questions
              </h2>
              <p className="text-black text-[24px] leading-[43px] max-w-[930px]">
                Everything you need to know about how TeamGrid works and the value it delivers.
              </p>
            </div>
            <div className="w-full max-w-[1053px] flex flex-col gap-5">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="privacy-faq-card overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(i)}
                    className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-6 text-left"
                  >
                    <span className="font-medium text-black text-[23px] leading-[35px]">{faq.q}</span>
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8">
                      {faqOpen[i] ? (
                        <Minus className="w-6 h-6 text-black" />
                      ) : (
                        <Plus className="w-6 h-6 text-black" />
                      )}
                    </span>
                  </button>
                  {faqOpen[i] && (
                    <div className="px-6 sm:px-8 pb-6 pt-0">
                      <p className="text-black text-[20px] leading-[33px]">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
        </div>

        <ReadyToTransform />
        <Footer />
      </div>

      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 rounded-xl flex items-center justify-center z-50"
          style={{
            background: TEAL,
            boxShadow: "0px 4px 12px rgba(0, 209, 193, 0.3)",
          }}
        >
          <ArrowUp className="w-6 h-6 text-black" strokeWidth={2} />
        </motion.button>
      )}
    </div>
    </>
  );
};

export default Privacy;
